<template>
  <div class="hero">
    <HeroCarousel class="hero__carousel" :activeIndex="activeIndex" />
    <div class="hero__wrapper">
      <div class="hero__wrapper__carousel-interface">
        <CarouselInterface
          :itemCount="2"
          :vertical="!mediaSmall"
          :heroHorizontal="mediaSmall"
          :currentIndex="activeIndex"
          :autoAdvance="false"
          @slideChange="handleSlideChange"
          v-if="!mediaSmall"
        />
      </div>
      <section class="hero__wrapper__content">
        <p class="hero__wrapper__content__eyebrow" v-if="!showCountdown">
          {{eyebrowCopy}}
        </p>
        <CountdownDisplay class="hero__wrapper__content__countdown" v-if="showCountdown" />
        <h2 class="hero__wrapper__content__title" v-html="headline">
        </h2>
        <p class="hero__wrapper__content__copy hero__wrapper__content__copy--first"
          v-html="bodyCopy1">
        </p>
        <p class="hero__wrapper__content__copy" v-html="bodyCopy2">

        </p>

        <div class="hero__wrapper__content__disclaimer"
          v-if="mediaSmall"
          @click="smallDisclaimerExpanded = !smallDisclaimerExpanded">
          <InfoIcon class="hero__wrapper__content__disclaimer-icon"/> {{disclaimerCta}}
        </div>

        <transition mode="out-in" name="fade">
          <RulesDisplay
            class="hero__wrapper__content__rules"
            collapsible
            v-if="smallDisclaimerExpanded"
          />
          <div class="hero__wrapper__content__buttons" v-else>
            <solid-button
              @click.capture.native="handleEnterClick"
              :to="{ name: 'entryForm' }"
              buttonIcon="chevron_right"
              class="hero__wrapper__content__buttons--solid"
              v-if="contestActive"
            >
              {{buttonCopy}}
            </solid-button>
            <outline-button buttonIcon="play_arrow" @click="handleWatchClick">
              {{watchCopy}}
            </outline-button>
          </div>
        </transition>

        <RulesDisplay
          class="hero__wrapper__content__rules__block"
          v-if="!mediaSmall"
        />

      </section>
    </div>
    <div class="hero__scroll-container">
      <p class="hero__scroll-container__copy">
        {{scrollCTACopy}}
      </p>
      <outline-button
        class="hero__scroll-container__scroll-button"
        buttonIcon="expand_more"
        iconOnly
        @click="handleScrollClick"
      />
      <div class="hero__scroll-container__scroll-line" />
    </div>
    <ScalingBackground
      class="hero__smoke"
      :largeWidth="backgroundLargeWidth"
      :largeHeight="backgroundLargeHeight"
      :smallWidth="backgroundSmallWidth"
      :smallHeight="backgroundSmallHeight"
    />
    <div class="hero__social-icons-component">
      <SocialFollowIcons />
    </div>
  </div>
</template>

<script>
/**
 * Hero Section.
 */
import { mapState, mapMutations, mapGetters } from 'vuex';
import OutlineButton from './../components/rb-button-outline';
import SolidButton from './../components/rb-button-solid';
import CarouselInterface from './../components/carousel-interface';
import HeroCarousel from './../components/hero-carousel';
import SocialFollowIcons from './../components/social-follow-icons';
import ScalingBackground from './../components/scaling-background';
import RulesDisplay from './../components/rules-display';
import InfoIcon from './../components/info-icon';
import CountdownDisplay from './../components/countdown-display';
import tracking from './../core/tracking';

import { UI_HERO_VIDEO_VISIBLE } from '../core/store/mutation-types';
import {
  CONTENT_HERO_BODY_1,
  CONTENT_HERO_BODY_2,
  CONTENT_HERO_HEADLINE,
  SIZE_WIDTH_HERO_SECTION_SMOKE_LARGE,
  SIZE_WIDTH_HERO_SECTION_SMOKE_SMALL,
  SIZE_HEIGHT_HERO_SECTION_SMOKE_LARGE,
  CONTENT_HERO_EYEBROW,
  CONTENT_HERO_WATCH_CTA,
  CONTENT_HERO_ENTER_CTA,
  CONTENT_HERO_SCROLL_CTA_MOBILE,
  CONTENT_HERO_SCROLL_CTA_BASE,
  CONTENT_GENERAL_RULES_TITLE
} from '../../design-tokens/build/dictionary';
import { getNumberFromToken } from '../core/utils';

export default {
  name: 'hero',
  data() {
    return {
      activeIndex: 0,
      itemCount: 2,
      headline: CONTENT_HERO_HEADLINE,
      bodyCopy1: CONTENT_HERO_BODY_1,
      bodyCopy2: CONTENT_HERO_BODY_2,
      eyebrowCopy: CONTENT_HERO_EYEBROW,
      watchCopy: CONTENT_HERO_WATCH_CTA,
      buttonCopy: CONTENT_HERO_ENTER_CTA,
      smallDisclaimerExpanded: false,
      disclaimerCta: CONTENT_GENERAL_RULES_TITLE,
    };
  },
  components: {
    OutlineButton,
    SolidButton,
    SocialFollowIcons,
    CarouselInterface,
    HeroCarousel,
    ScalingBackground,
    RulesDisplay,
    InfoIcon,
    CountdownDisplay,
  },
  computed: {
    backgroundLargeWidth() {
      return getNumberFromToken(SIZE_WIDTH_HERO_SECTION_SMOKE_LARGE);
    },
    backgroundSmallWidth() {
      return getNumberFromToken(SIZE_WIDTH_HERO_SECTION_SMOKE_SMALL);
    },
    backgroundLargeHeight() {
      return getNumberFromToken(SIZE_HEIGHT_HERO_SECTION_SMOKE_LARGE);
    },
    backgroundSmallHeight() {
      return getNumberFromToken(SIZE_HEIGHT_HERO_SECTION_SMOKE_LARGE);
    },
    scrollCTACopy() {
      return this.isTouchCentric ? CONTENT_HERO_SCROLL_CTA_MOBILE : CONTENT_HERO_SCROLL_CTA_BASE;
    },
    ...mapGetters('windowMedia', [
      'isTouchCentric',
    ]),
    ...mapState('windowMedia', [
      'mediaSmall'
    ]),
    ...mapGetters('ui', [
      'contestActive',
      'showCountdown'
    ]),
  },
  watch: {
    mediaSmall(value) {
      if (!value) {
        // close the small disclaimer if screen size changes
        this.smallDisclaimerExpanded = false;
      }
    }
  },
  methods: {
    /**
     * increment the index of the current slide
     *
     * @param {String} direction - prev or next
     */
    handleSlideChange(direction) {
      if (direction === 'next') {
        if (this.activeIndex === this.itemCount - 1) {
          this.activeIndex = 0;
        } else {
          this.activeIndex += 1;
        }
      } else if (this.activeIndex === 0) {
        this.activeIndex = this.itemCount - 1;
      } else {
        this.activeIndex -= 1;
      }
    },
    handleWatchClick() {
      this.setHeroVideoVisible({
        visible: true
      });
    },
    handleScrollClick() {
      this.$emit('scroll');
    },
    handleEnterClick() {
      tracking.trackEvent('e-rb-hero-1');
    },
    ...mapMutations({
      setHeroVideoVisible: UI_HERO_VIDEO_VISIBLE,
    })
  },
};
</script>

<style lang="scss">
$z-index-hero-wrapper: 3;
$z-index-hero-carousel: 1;
$z-index-hero-smoke: $z-index-hero-carousel + 1;
.hero {
  position: relative;
  margin: $size-margin-top-hero-section-large 0 $size-margin-bottom-hero-section-large;
  display: flex;
  align-items: center;
  min-height: 93.2rem;

  @media only screen and (max-width: 948px) {
    min-height: 70rem;
  }

  max-height: $size-max-height-hero-section-large;
  @include media(small) {
    margin: $size-margin-top-hero-section-small 0 $size-margin-bottom-hero-section-small;
    height: 90rem;
    max-height: $size-max-height-hero-section-small;
  }
  &__wrapper {
    @include content-area;
    position: absolute;
    z-index: $z-index-hero-wrapper;
    top: 0;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    @include media(small) {
      top: 62%;
    }
    &__content {
      max-width: 50%;
      min-width: $size-width-content-min-width;
      height: 100%;
      @media screen and (max-width: 900px) {
        margin-left: -5rem;
      }
      @include media(small) {
        width: 100%;
        max-width: 100%;
        text-align: center;
        margin-left: 0;
        min-width: auto;
        padding: 0.6rem;
      }
      &__eyebrow {
        @include eyebrow-text;
      }
      &__countdown,
      &__eyebrow {
        margin-bottom: $size-margin-bottom-hero-scroll-copy;
        @include media(small) {
          margin-bottom: $size-margin-bottom-hero-eyebrow-small;
        }
      }

      &__title {
        @include heading-1;
        @include media(medium) {
          max-width: $size-width-hero-title-medium;
        }
        @include media(small) {
          margin: 0 auto;
        }
      }
      &__copy {
        @include body-copy-regular;
        max-width: $size-width-hero-copy-base;
        margin-top: $size-margin-top-hero-copy;
        margin-bottom: $size-margin-bottom-hero-scroll-copy;
        @include media(medium) {
          max-width: $size-width-hero-copy-medium;
          @include body-copy-x-small;
          margin-top: $size-margin-top-hero-copy;

        }
        @include media(small) {
          margin: $size-margin-top-hero-copy auto;
          max-width: $size-width-hero-copy-small;
          &--first {
            padding: $size-padding-hero-copy-small $size-padding-hero-copy-small 0 $size-padding-hero-copy-small;
          }
        }
      }
      &__disclaimer {
        @include body-copy-footnote;
        margin: 2rem 0;
        cursor: pointer;
        button.info-icon.hero__wrapper__content__disclaimer-icon {
            height: 1.3rem;
            width: 1.3rem;
            font-size: 0.9rem;
            padding: 0;
            vertical-align: top;
            margin-top: 0.2rem;
        }
        @include global-media(small) {
          // margin: $size-margin-top-hero-section-disclaimer-small auto 0;
        }
      }

      &__buttons {
        margin-top: 50px;
        display: flex;
        align-items: center;
        @include media(small) {
          justify-content: center;
        }
        &--solid {
          margin-right: 40px;
        }
      }

      &__rules,
      &__buttons {
        @include global-media(small) {
          // margin: $size-margin-top-hero-section-disclaimer-small auto 0;
          margin-top: 0;
          height: 6rem;
        }
      }

      &__rules__block {
        margin-top: $size-margin-top-hero-section-disclaimer-large;
        max-width: $size-max-width-rules-display-large + $size-padding-rules-display-hero-large;
        padding-right: $size-padding-rules-display-hero-large;
      }
    }
    &__carousel-interface {
      top: 1rem;
      left: 0;
      height: 100%;
      position: absolute;
      margin-left: 3.4rem;
      max-height: 45.5rem;
      @media screen and (max-width: 89.9375em) and (min-width: 59.375em) {
        margin-left: 0rem;
      }
      @media screen and (max-width: 100rem) {
        margin-left: 0rem;
      }
      @include media(small) {
        height: auto;
        top: auto;
        bottom: -8rem;
        width: 100%;
        left: 50%;
        transform: translateX(-50%);
        max-width: 45.6rem;
        justify-content: space-between;
      }
    }
  }
  &__social-icons-component {
    margin: 0;
    top: 20%;
    right: 5rem;
    z-index: 3;
    position: absolute;
    @media screen and (max-width: 89.9375em) and (min-width: 59.375em) {
      right: 2rem;
    }
    @media only screen and (max-width: 948px) {
      display: none;
    }
  }

  &__scroll-container {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    position: absolute;
    bottom: 0%;
    z-index: $z-index-hero-wrapper;
    margin-bottom: -1%;
    // bottom: 200px;
    left: 50%;
    transform: translateX(-50%);
    &__copy {
      @include body-copy-x-small;
      margin-bottom: $size-margin-bottom-hero-scroll-copy;
    }
    &__scroll-button {
      flex-shrink: 0;
    }
    &__scroll-line {
      margin-top: $size-margin-top-hero-scroll-line;
      height: $size-height-hero-scroll-line;
      width: $size-width-hero-scroll-line;
      background-color: $color-base-ninja-yellow-base;
    }
    @include global-media(medium-hero) {
      height: 20%;
      bottom: $size-bottom-hero-scroll-container-medium;
      &__scroll-line {
        height: 25%;
      }
    }
    @include media(medium) {
      &__scroll-line {
        display: none;
      }
    }
    @include media(small) {
      &__scroll-line {
        display: block;
        height: $size-height-hero-scroll-line;
      }
    }
  }

  &__carousel {
    z-index: $z-index-hero-carousel;
  }
  & &__smoke {
    background-image: url('./../assets/images/smoke-bkg.png');
    bottom: 0;
    z-index: $z-index-hero-smoke;
    transform: translatey(20%);
  }
}
</style>
